import styles from "./DealerAnalytics.module.scss";
import { BarList } from "./charts";
import { Card, Empty, ReportFrame, SortTable, StatTile, fmtDate, fmtNum, useReport } from "./shared";

const FILTER_LABELS = {
  make: "Make", model: "Model", grade: "Grade", modelDetail: "Chassis", chassisGeneration: "Chassis generation",
  minYear: "Year from", maxYear: "Year to", minPrice: "Price from", maxPrice: "Price to",
  minMileage: "Mileage from", maxMileage: "Mileage to", transmission: "Transmission", color: "Colour",
  bodyType: "Body type", drive: "Drive", fuel: "Fuel", importable: "Importable now", search: "Search box",
};
const filterLabel = (f) => FILTER_LABELS[f] || f;

const Searches = ({ range, refreshKey }) => {
  const state = useReport("searches", range, refreshKey);
  return (
    <ReportFrame state={state}>
      {(d) => {
        const t = d.totals;
        if (!t.searches) {
          return (
            <div className={styles.notice}>
              No dealer-stock searches recorded in this period. Searches are counted once the tracking update is live on
              www.importjdmmagic.com and the portal; every search or filter applied on either dealer-stock page lands here.
            </div>
          );
        }
        return (
          <div className={styles.stack}>
            <div className={styles.tiles}>
              <StatTile label="Searches" value={fmtNum(t.searches)} sub="searches and filters applied" />
              <StatTile label="People searching" value={fmtNum(t.visitors)} sub="by account, or by network when signed out" />
              <StatTile label="Found nothing" value={fmtNum(t.zeroResults)} sub={`${t.zeroShare ?? "–"}% of searches`} />
              <StatTile label="By site" value={`${fmtNum(t.www)} / ${fmtNum(t.portal)}`} sub="www / portal" />
            </div>

            <Card
              title="Searches that found nothing"
              sub="Cars people asked for that we could not show them. Ranked by how many people tried: a sourcing list and a list of pages to build."
            >
              <SortTable
                rowKey={(r) => `${r.query}|${r.make}|${r.model}|${(r.filters || []).join(",")}`}
                rows={d.zeroResults}
                initialSort={{ key: "visitors", dir: "desc" }}
                empty="Every search found something."
                columns={[
                  { key: "query", label: "Search", render: (r) => r.query || <span className={styles.muted}>(filters only)</span> },
                  { key: "make", label: "Make / model", sort: (r) => `${r.make} ${r.model}`, render: (r) => [r.make, r.model].filter(Boolean).join(" ") || "–" },
                  { key: "filters", label: "Filters", sortable: false, render: (r) => (r.filters || []).map((f) => <span key={f} className={styles.chip} style={{ marginRight: 4 }}>{filterLabel(f)}</span>) },
                  { key: "visitors", label: "People", num: true, bar: true },
                  { key: "count", label: "Times", num: true },
                  { key: "last", label: "Last", num: true, sort: (r) => new Date(r.last).getTime(), render: (r) => fmtDate(r.last) },
                ]}
              />
            </Card>

            <div className={styles.grid2}>
              <Card title="What people type" sub="The search box, lower-cased.">
                <SortTable
                  rowKey={(r) => r.query}
                  rows={d.queries}
                  initialSort={{ key: "count", dir: "desc" }}
                  columns={[
                    { key: "query", label: "Search term" },
                    { key: "count", label: "Times", num: true, bar: true },
                    { key: "visitors", label: "People", num: true },
                    { key: "avgResults", label: "Avg results", num: true },
                    { key: "zero", label: "Found nothing", num: true },
                  ]}
                />
              </Card>
              <Card title="Makes and models filtered on">
                <SortTable
                  rowKey={(r) => `${r.make}|${r.model}`}
                  rows={d.makeModels}
                  initialSort={{ key: "count", dir: "desc" }}
                  columns={[
                    { key: "make", label: "Make / model", sort: (r) => `${r.make} ${r.model || ""}`, render: (r) => <>{r.make} {r.model || <span className={styles.muted}>(any model)</span>}</> },
                    { key: "count", label: "Times", num: true, bar: true },
                    { key: "visitors", label: "People", num: true },
                    { key: "zero", label: "Found nothing", num: true },
                  ]}
                />
              </Card>
            </div>

            <Card title="Filters people use" sub="How often each filter was part of a search.">
              {d.filters.length
                ? <BarList rows={d.filters.map((f) => ({ key: f.filter, label: filterLabel(f.filter), value: f.count }))} />
                : <Empty>No filters used.</Empty>}
            </Card>
          </div>
        );
      }}
    </ReportFrame>
  );
};

export default Searches;
